import { _longPressTypes, LongPressContext } from 'global/wrappers/LongPressWrapper';
import _ from 'lodash';
import React, { useContext, useEffect } from 'react';
import { Menu, Message } from 'semantic-ui-react';
import { SettingParentContext } from '../cnr/contexts/SettingParentContext';
import { ViewSettingsComponentContext } from '../cnr/contexts/ViewSettingsComponentContext';
import { ViewSettingsContext } from '../cnr/contexts/ViewSettingsContext';
import { ViewSettingsMenuContext } from '../cnr/contexts/ViewSettingsMenuContext';
import { creatingHelpers } from '../common/creating';
import { _ignoreKeys } from '../common/dataFix';
import { uniqueKey } from '../common/keys';
import DragDropper, { dragDropperTypes } from '../dragNDrops/DragDropper';
import { gEnums } from '../enums/globalEnums';
import MenuItemSingle from './components/menus/MenuItemSingle';
import { allowShow, setUpdateProps } from './components/menus/menuItemHelpers';
import HeaderDropdown from './headers/HeaderDropdown';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';

/**
 * @calledFrom ViewSettingsMenuProvider ONLY
 * @returns a Menu (vertical) with menuItems
 * @description Contains all the viewSettings information.
 */
const ViewSettingsMenu = () => {

  // settingsParentContext
  const settingsParentContext = useContext(SettingParentContext)
  const { states, handlers, settings } = settingsParentContext ?? {}
  const { appSettings_state, framework_state, currentSettings_state } = states ?? {}
  const { appSettings_handlers } = handlers ?? {}
  const { homeSettings } = settings ?? {}
  const { global: global_home } = homeSettings ?? {}
  const { settingsOptions: settingsOptions_home } = global_home ?? {}
  const { helpOn, themeSelected } = appSettings_state ?? {}
  const { desktopMode } = framework_state ?? {}
  const { aps_viewItems, aps_styles } = _.get(currentSettings_state, 'gvviSettings', {});
  const { useDarkMode, showHelpLine, sortMenuItemsByPermission } = settingsOptions_home ?? {}
  const appUserAccess = useAppUserAccess()

  const longPressContext = useContext(LongPressContext);
  const { longPress_state } = longPressContext ?? {}
  const { directViewItem } = longPress_state ?? {}

  // viewSettingsSwipeContext - THIS has all the info
  const viewSettingsContext = useContext(ViewSettingsContext);
  const { viewSettings_state, viewSettings_handlers } = viewSettingsContext ?? {}
  const { settings_temp, isGv, showViewLinking } = viewSettings_state ?? {}
  const { display } = settings_temp ?? {}
  const { layoutType } = display ?? {}

  // viewSettingsComponentContext 
  const viewSettingsComponentContext = useContext(ViewSettingsComponentContext);
  const { vsc_state, vsc_handlers } = viewSettingsComponentContext ?? {}
  const { isGlobal, swipedItem, updateProps } = vsc_state ?? {}
  const { swipeToSelected } = vsc_handlers ?? {}

  // IMPORTANT: Settings - activeMenuItems are the menu item for this current menu    
  // viewSettingsMenuContext
  const viewSettingsMenuContext = useContext(ViewSettingsMenuContext);
  const { vsm_state } = viewSettingsMenuContext ?? {}

  const {
    activeMenuItems,
    menuItemAllows,
    menuType,
    menuTypeKey,
    otherData,
    propsOnly,
    settingsMessage,
    showSort,
  } = vsm_state ?? {}

  const cn_menu = 'view-settings-menu msmi '
  let cn_portal = 'msmi three menu-groups menu'

  switch (menuType) {
    case gEnums.menuTypes.viewItem:
    case gEnums.menuTypes.propItem:
    case gEnums.menuTypes.propSections:
      cn_portal = 'msmi four menu-groups menu'
  }

  useEffect(() => {
    const activeMenuItem = _.find(activeMenuItems, { key: directViewItem })
    if (activeMenuItem) {
      clickTo(activeMenuItem, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directViewItem]);

  const clickTo = (item, allowClick) => {
    if (!allowClick) { return false }
    const { show, showDesktop } = item ?? {}
    const _show = desktopMode ? showDesktop : show
    if (allowShow(menuTypeKey)) { if (!_show) { return false } }
    const swipeProps = {
      swipedItem: item,
      menuType
    }
    // the sidebar is already open, add to it  
    if (swipeToSelected) {
      swipeToSelected(swipeProps)
    } else {
      console.log('swipeToSelected is not available', vsc_handlers)
    }
  }

  const themeSelectClick = (e, item) => {
    e.preventDefault()
    e.stopPropagation()
    appSettings_handlers.handleThemeSelect(item.parentSettingKey)
  }

  /** handles the click of any of the list items in viewSettingsGrid */
  const clickCheck = (e, menuItem, allowClick, checkType, subProp) => {
    if (!allowClick) { return false }
    e.preventDefault()
    e.stopPropagation()
    switch (checkType) {
      case gEnums.menuItemOptionTypes.highlight:
        // nothing
        break;
      default:
        const { handleFormDataChange_settings } = viewSettings_handlers
        if (handleFormDataChange_settings) {
          const ups = setUpdateProps(updateProps, menuItem, menuType, subProp, checkType)
          handleFormDataChange_settings(null, ups)
        }
    }
  }

  /** handles the click of the option dd of the menuItem */
  const clickOption = (clickOptions, e) => {
    const { menuItem, allowClick, clickOptionType } = clickOptions
    if (!allowClick) { return false }
    e.preventDefault()
    // e.stopPropagation()  
    switch (clickOptionType) {
      default:
        const { handleFormDataChange_settings } = viewSettings_handlers
        if (handleFormDataChange_settings) {
          const ups = setUpdateProps(updateProps, menuItem, menuType, null, clickOptionType)
          // formData, updateProps
          handleFormDataChange_settings(null, ups)
        }
    }
  }

  // back from DragDropper 
  const handleSorted = (sortedGroups) => {
    if (sortedGroups) {
      const _sortedGroups = {}
      _.forEach(sortedGroups, (sortedGroup, sgKey) => {
        const dis = sortedGroup.dataItems
        _sortedGroups[sgKey] = creatingHelpers.createObject(dis, 'key')
      })
      viewSettings_handlers.handleGroupSort(updateProps, _sortedGroups, menuType)
    }
  }

  const handleSorted_dataItems = (dataItems) => {
    viewSettings_handlers.handleGroupItemsSort(updateProps, dataItems, menuType)
  }

  let allowSort = false

  switch (menuType) {
    case gEnums.menuTypes.viewItem:
      allowSort = !isGlobal
      break;
    case gEnums.menuTypes.propItem:
    case gEnums.menuTypes.propSections:
      allowSort = true
      break;
    default:
    // nothing
  }

  const grProps = {
    aps_viewItems,
    clicks: { clickCheck, clickTo, clickOption },
    desktopMode,
    helpOn,
    isGlobal,
    isGv,
    menuType,
    showViewLinking,
    themeSelectClick,
    themeSelected,
  }

  const styleAndClass = aps_styles?.[gEnums?.projectStyles?.gridViewItemIcons] ?? { style: {} };
  const sac = { ...styleAndClass }

  const setAllowClick = (item) => {
    let allowClick = true
    if (!isGlobal) {
      switch (layoutType) {
        case gEnums.layoutTypes.icons:
          if (updateProps && updateProps.vit) {
            switch (item.key) {
              case 'auth':
              case 'display':
                break;
              default:
                allowClick = false
            }
          }
          break;
        default:
        // nothing
      }
    }
    return allowClick
  }

  // SORTING
  /**
   * 
   * @param {array} menuItems 
   * @returns the sorted list of menu items 
   */
  const getSortedMenuItems = (menuItems) => {
    switch (menuTypeKey) {
      case 'props':
      case 'propSections':
      case 'viewItems':
        return _.sortBy(menuItems, 'position')
      default:
        return _.sortBy(menuItems, 'caption')
    }
  }

  /**
   * 
   * @param {object} menuItem 
   * @param {string} key 
   * @returns a `MenuItemSingle`
   */
  const menuItemSingle = (menuItem, key) => {
    const _menuItem = _.cloneDeep(menuItem)
    let allowClick = setAllowClick(_menuItem)
    // if (_menuItem) { updateMenuItem(_menuItem, menuType) }
    // console.log('menuItemSingle', _menuItem)
    let { bc, c, restrict, settingsAuthLevel } = _menuItem
    const saci = { className: sac.className }
    if (bc || c) { saci.style = {} }
    if (bc) {
      saci.className = saci.className.replace('bc-settings', '')
      saci.style.backgroundColor = bc
    }
    if (c) { saci.style.color = c }
    const x = { allowClick, menuItem: _menuItem, grProps, isGlobal, restrict, settingsAuthLevel, menuItemAllows, useDarkMode, showHelpLine }
    return <MenuItemSingle key={uniqueKey('vsm', 'mis', key)} {...x} />
  }

  /**
   * IMPORTANT - Settings: Menu Items
   * @param {array} menuItems 
   * @returns a List of all the view items
   */
  const menuMenuItems = (menuItems) => {
    const x = {}
    const menuItems_sorted = getSortedMenuItems(menuItems)
    // eslint-disable-next-line  
    const segs = _.map(menuItems_sorted, (menuItem, miKey) => {
      const { key: key_mi, settingsAuthLevel, position } = menuItem ?? {}
      let allow = true
      if (settingsAuthLevel && (appUserAccess.accessLevel < settingsAuthLevel)) { allow = false }
      if (_ignoreKeys.includes(key_mi)) { allow = false }
      if (allow) {
        if (showSort) {
          x[key_mi] = {
            item: menuItem,
            position: position ? position : index,
            content: menuItemSingle(menuItem, miKey)
          }
        } else {
          return menuItemSingle(menuItem, miKey)
        }
      }
    })
    if (showSort) {
      return x
    } else {
      return segs
    }
  }

  const sectionProps = (otherData) => {
    // group the menuItems by propSection 
    const grps = _.groupBy(activeMenuItems, 'propSection')
    // sort the groups by position 
    const od = reorderSections(otherData)
    const xx = {}
    Object.keys(od).forEach(key => { xx[key] = grps[key] })
    return xx
  }

  /** DragDropper container will a header dropdown and dragable items */
  const groupContainerDd = (groups) => {

    const _dndGroups = {}

    _.forEach(groups, (group, groupKey) => {
      const dataItems = group ?? {}
      const elements = menuMenuItems(dataItems)
      const hdd = <HeaderDropdown key={uniqueKey('vsm', 'gc', 'hdd')} dataHeaderType={gEnums.dataHeaderTypes.sectionHeader} clickOption={clickOption} opts={{ groupKey, iconSize: 'small' }} />
      _dndGroups[groupKey] = { elements, dataItems, hdd }
    })

    return <DragDropper
      ddType={'viewItems'}
      dndType={dragDropperTypes.viewSettingMenu}
      dndGroups={_dndGroups}
      handleSorted={handleSorted}
      handleSorted_dataItems={handleSorted_dataItems}
      groupChangeKey={'propSection'}
      otherData={otherData}
      allowSort={true}
      portalCn={cn_portal}
    />
  }

  const menuMenuItems_sort = (groupKey, menuItems) => {
    const dndGroups = {
      [groupKey]: {
        dataItems: menuItems,
        elements: menuMenuItems(menuItems),
      }
    }

    return <DragDropper
      dndType={dragDropperTypes.viewSettingMenu}
      dndGroups={dndGroups}
      handleSorted={handleSorted}
      handleSorted_dataItems={handleSorted_dataItems}
      allowSort={true}
      portalCn={cn_portal}
    />
  }

  /**
   * 
   * @param {array} menuItems (caption, icon, key, pit, etc.)
   * @returns a group of `menu items` based on certain parameters 
   */
  const menuItemElements = (menuItems) => {
    if (!propsOnly) {
      const groupArray = []
      const menuItems_propSection = _.groupBy(menuItems, 'propSection')
      if (menuItems_propSection) {
        if (otherData) {
          const groups = sectionProps(otherData)
          return groupContainerDd(groups)
        } else {
          return groupContainerDd(menuItems_propSection)
        }
      }
      return groupArray
    } else {
      const groupKey = swipedItem && swipedItem.settingKey ? swipedItem.settingKey : swipedItem ? swipedItem.key : 'none'
      if (allowSort) {
        return menuMenuItems_sort(groupKey, menuItems)
      } else {
        // for basic pageItems
        return menuMenuItems(menuItems)
      }
    }
  }

  const parentMessage = () => <Message
    warning
    size='tiny'
    header='Settings'
    content={settingsMessage}
  />

  /**
   * 
   * @param {object} groupedMenuItem 
   * @param {string} cnsmi 
   * @returns a `Menu`
   */
  const menu = (groupedMenuItem, cnsmi, key_settingsAuthLevel) => <Menu className={cn_menu + cnsmi} key={uniqueKey('vsm', 'grp', key_settingsAuthLevel)} borderless inverted={useDarkMode} vertical fluid >
    {groupedMenuItem && menuItemElements(groupedMenuItem)}
  </Menu>

  const content = () => {

    let cnsmi = (menuItemAllows.useAllow || menuItemAllows.color) ? 'msmi menu-groups three' : 'msmi menu-groups three'

    if (sortMenuItemsByPermission || 1 === 1) {
      cnsmi += ' padd0 mar0'

      const groupedMenuItems = _.groupBy(activeMenuItems, 'settingsAuthLevel')
      const menus = []

      // the `undefined` items do NOT have a `settingsAuthLevel`. This is NOT an error
      if (groupedMenuItems.undefined) { menus.push(menu(groupedMenuItems.undefined, cnsmi)) }

      _.forEach(groupedMenuItems, (gmi, gmiKey) => {
        switch (gmiKey) {
          case 'undefined':
            break;
          default:
            // const groupedMenuItem = groupedMenuItems[key_settingsAuthLevel]
            menus.push(menu(gmi, cnsmi, gmiKey))
            break;
        }
      })

      return <React.Fragment>
        {settingsMessage && parentMessage()}
        {menus}
      </React.Fragment>
    } else {
      return <React.Fragment>
        {settingsMessage && parentMessage()}
        <Menu borderless inverted={useDarkMode} vertical fluid className={cn_menu + cnsmi}>
          {activeMenuItems && menuItemElements(activeMenuItems)}
        </Menu>
      </React.Fragment>
    }

  }

  if (!_.isEmpty(activeMenuItems)) {
    return content(activeMenuItems)
  } else {
    return <div style={{ padding: '1em' }}>No Active Menu Items</div>
  }
}

export default ViewSettingsMenu


const reorderSections = (propSections) => {

  _.forEach(propSections, (propSection, psKey) => {
    propSection.key = psKey;
    propSection._itemKey = psKey;
  });

  // 1️⃣ Normalize and apply the 'hidden' rule
  const _normalizedPropSections = _.map(propSections, (propSection, psKey) => ({
    ...propSection,
    show: propSection.key === 'hidden' ? false : !!propSection.show,
  }));

  // 2️⃣ Sort logic:
  // - visible (show=true) first
  // - custom ordering: description → before hidden
  // - by position
  const _sortedPropSections = _.orderBy(
    _normalizedPropSections,
    [
      // show=true first
      (s) => (s.show ? 0 : 1),

      // priority order: normal=0, description=1, hidden=2
      (s) => (s.key === 'hidden' ? 2 : s.key === 'description' ? 1 : 0),

      // position
      'position',
    ],
    ['asc', 'asc', 'asc']
  );

  const _sps = {}
  _.forEach(_sortedPropSections, (sps) => {
    _sps[sps.key] = sps
  })

  return _sps


  // 3️⃣ Group viewProps by their propSection
  // const _viewPropsGrouped = _.groupBy(viewProps, 'propSection');

  // 4️⃣ Build object keyed by position  
  // const propSectionProps = _sortedPropSections.map((propSection) => ({
  //   section: propSection,
  //   items: _.sortBy(
  //     _viewPropsGrouped[propSection.key || propSection.id || propSection.propSection] || [],
  //     'position'
  //   )

  // }))

}